/**
 * NOT CHECKED
 */
import React, { useEffect, useState } from 'react';
import cn from 'class-names';

import Input from '@uikit/Form/Input/Input';

import { SVGIcon } from '@uikit/old/Icon/Icon';
import Select from '@uikit/Form/Select/Select';
import Display from '@components/Display/Display';

import './Pagination.scss';

type PaginationPropsType = {
  curPage: number;
  isUnknownNexBehaviour?: boolean;
  isNextDisabled?: boolean;
  withLimitOptions?: boolean;
  onChangePage: (number) => void;
  pageLimit?: number;
  limitOptions?: number[];
  onChangePageLimit?: (number) => void;
  totalItems?: number;
  maxKnownPage?: number;
  pageSelectAllowed?: boolean;
  /**
   * data-testid on the pagination root; derived: `-first`, `-prev`, `-next`, `-limit` (the page size Select),
   * `-page` (the page number input) and `-last-page`. Nothing extra when absent.
   */
  testId?: string;
};

export const DEFAULT_LIMIT_OPTIONS = [10, 20, 50, 100];

const getUnknownPagesList = (curPage) => {
  if (curPage > 3) {
    return [1, '...', curPage];
  }

  const result = [];

  for (let i = 1; i <= curPage; i++) {
    result.push(i);
  }

  return result;
};

const getRestPages = (curPage, lastPage) => {
  if (lastPage - curPage === 0 || lastPage < curPage) {
    return [];
  }

  if (lastPage - curPage === 1) {
    return [lastPage];
  }

  return ['...', lastPage];
};

const PageButton = ({ curPage, pageValue, onClick }) => {
  const getCnForPageBtn = (page) =>
    cn('pagination__button pagination__button--page-btn', {
      'pagination__button--active': page === curPage,
      'pagination__button--not-button': !Number.isInteger(page),
    });

  const handlePageClick = () => {
    if (Number.isInteger(pageValue)) {
      onClick(pageValue);
    }
  };

  return (
    <button type="button" className={getCnForPageBtn(pageValue)} onClick={handlePageClick}>
      {pageValue}
    </button>
  );
};

function LimitSelect({ pageLimit, limitOptions, onChangePageLimit, testId = undefined }) {
  const options = limitOptions?.length ? limitOptions : DEFAULT_LIMIT_OPTIONS;

  const handleChange = ({ value }) => {
    if (onChangePageLimit) {
      onChangePageLimit(value);
    }
  };

  return (
    <div className="pagination__limit-select">
      <span>Show:</span>
      <Select
        menuPlacement="top"
        value={{ value: pageLimit, label: pageLimit }}
        options={options.map((option) => ({ value: option, label: option }))}
        onChange={handleChange}
        testId={testId}
      />
      <span>per page</span>
    </div>
  );
}

const UnknownNextPagination = ({ curPage, onChangePage, isNextDisabled, maxKnownPage }) => {
  const cnPrevPageBtn = cn('pagination__button pagination__button--edge-btn', {
    'pagination__button--disabled': curPage === 1,
  });

  const cnNextPageBtn = cn('pagination__button pagination__button--edge-btn', {
    'pagination__button--disabled': isNextDisabled,
  });

  const handleNextClick = () => {
    if (!isNextDisabled) {
      onChangePage(curPage + 1);
    }
  };
  const handlePrevClick = () => {
    if (curPage > 1) {
      onChangePage(curPage - 1);
    }
  };
  const handlePageClick = (page) => onChangePage(page);

  return (
    <div className="pagination-wrapper">
      <div className="pagination">
        <button onClick={handlePrevClick} type="button" className={cnPrevPageBtn}>
          <SVGIcon icon="prevPage" />
        </button>
        {getUnknownPagesList(curPage).map((page) => (
          <PageButton key={page} curPage={curPage} pageValue={page} onClick={handlePageClick} />
        ))}
        {getRestPages(curPage, maxKnownPage).map((page) => (
          <PageButton key={page} curPage={curPage} pageValue={page} onClick={handlePageClick} />
        ))}
        <button onClick={handleNextClick} type="button" className={cnNextPageBtn}>
          <SVGIcon icon="nextPage" />
        </button>
      </div>
    </div>
  );
};

const FullPagination = ({
  curPage,
  onChangePage,
  pageLimit,
  limitOptions,
  onChangePageLimit,
  totalItems,
  withLimitOptions,
  isUnknownNexBehaviour = false,
  isNextDisabled = false,
  pageSelectAllowed = true,
  testId = undefined,
}) => {
  const tid = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);
  const [jumpToPage, setJumpToPage] = useState<number>(curPage || 0);

  useEffect(() => {
    setJumpToPage(curPage);
  }, [curPage]);

  const handlePageClick = (page) => onChangePage(page);

  const lastPage = isUnknownNexBehaviour ? 10 : Math.ceil(totalItems / pageLimit);

  const prevBtnDisabled = curPage <= 1;
  const nextBtnDisabled = isNextDisabled || curPage === lastPage;

  const showPagination =
    !withLimitOptions || (withLimitOptions && (curPage !== 1 || totalItems > pageLimit));

  return (
    <div className="pagination-wrapper" data-testid={testId}>
      <Display isVisible={showPagination}>
        <div className="pagination-full-width">
          <div className="pagination__switch-buttons">
            <button
              onClick={() => (prevBtnDisabled ? null : handlePageClick(1))}
              data-testid={tid('first')}
              type="button"
              className={cn('pagination__button pagination__button--edge-btn', {
                'pagination__button--disabled': curPage === 1,
              })}
            >
              <SVGIcon icon="prevPage" />
            </button>

            <div
              className={cn('pagination__switch-buttons-button', {
                'pagination__switch-buttons-button--disabled': prevBtnDisabled,
              })}
              data-testid={tid('prev')}
              data-disabled={prevBtnDisabled ? 'true' : 'false'}
              onClick={() =>
                prevBtnDisabled ? null : handlePageClick(Math.max(Number(curPage) - 1, 1))
              }
            >
              Prev
            </div>
            <div
              className={cn('pagination__switch-buttons-button', {
                'pagination__switch-buttons-button--disabled': nextBtnDisabled,
              })}
              data-testid={tid('next')}
              data-disabled={nextBtnDisabled ? 'true' : 'false'}
              onClick={() =>
                nextBtnDisabled ? null : handlePageClick(Math.min(Number(curPage) + 1, lastPage))
              }
            >
              Next
            </div>
          </div>

          {withLimitOptions && onChangePageLimit && (
            <LimitSelect
              pageLimit={pageLimit}
              limitOptions={limitOptions}
              onChangePageLimit={onChangePageLimit}
              testId={tid('limit')}
            />
          )}

          <div className="pagination__select">
            <span>Page:</span>

            <Input
              disabled={isUnknownNexBehaviour || !pageSelectAllowed}
              type="number"
              min={1}
              max={pageLimit || 10}
              value={jumpToPage}
              data-testid={tid('page')}
              onChange={({ target: { value } }) => {
                if (value >= lastPage) {
                  setJumpToPage(lastPage);
                } else if (!!value && value <= 1) {
                  setJumpToPage(1);
                } else {
                  setJumpToPage(value);
                }
              }}
              onKeyDown={(e) => (e.keyCode === 13 ? handlePageClick(jumpToPage) : null)}
              placeholder=""
            />

            {!isUnknownNexBehaviour && pageSelectAllowed && (
              <span>
                of{' '}
                <span
                  className="pagination__select-last-page"
                  data-testid={tid('last-page')}
                  onClick={() => handlePageClick(lastPage)}
                >
                  {lastPage}
                </span>
              </span>
            )}
          </div>
        </div>
      </Display>
    </div>
  );
};

const Pagination = ({
  curPage,
  isUnknownNexBehaviour,
  onChangePage,
  isNextDisabled = false,
  pageLimit,
  limitOptions,
  onChangePageLimit,
  totalItems = 0,
  withLimitOptions = false,
  maxKnownPage = 0,
  pageSelectAllowed = true,
  testId,
}: PaginationPropsType) => {
  // if (isUnknownNexBehaviour) {
  //   return (
  //     <UnknownNextPagination
  //       isNextDisabled={isNextDisabled}
  //       curPage={curPage}
  //       onChangePage={onChangePage}
  //       maxKnownPage={maxKnownPage}
  //     />
  //   );
  // }

  return (
    <FullPagination
      totalItems={totalItems}
      pageLimit={pageLimit}
      limitOptions={limitOptions}
      onChangePageLimit={onChangePageLimit}
      curPage={curPage}
      onChangePage={onChangePage}
      withLimitOptions={withLimitOptions}
      isUnknownNexBehaviour={isUnknownNexBehaviour}
      isNextDisabled={isNextDisabled}
      pageSelectAllowed={pageSelectAllowed}
      testId={testId}
    />
  );
};

export default Pagination;
